import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { getActivityOrigin } from './use_activity_source_status.hook';
import { ActivityOrigin } from '../types';
/**
 * Hook to determine activity source status for multiple source activities.
 *
 * This hook takes source activity IDs directly and analyzes them to determine their origin (original, cloned, versioned, or cloned from another workspace)
 * based on their metadata. It does not fetch source-of-source activities, only calculates the status from the source activities themselves.
 *
 * @param sourceIds - Array of source activity IDs to check. These are treated as the activities to analyze. If undefined or empty, returns empty state.
 * @returns Object mapping each sourceId to its ActivitySourceStatus, including origin and source activity metadata (but not the actual source activity objects).
 *
 * @public
 */
export const useActivitySourceStatusBatch = (sourceIds) => {
    const { get } = useApiClient(ApiClientService.LIBRARY);
    // Filter out undefined/null IDs - these are source activity IDs
    const validSourceIds = useMemo(() => sourceIds?.filter((id) => !!id) || [], [sourceIds]);
    // Batch fetch source activities directly (these are the activities to analyze)
    const { data: batchActivityResponse, isLoading: isLoadingActivities } = useQuery({
        queryKey: ['activities', 'batch', validSourceIds],
        queryFn: async () => {
            if (validSourceIds.length === 0) {
                return { data: [] };
            }
            // Build query string with id[] parameters
            const queryParams = new URLSearchParams();
            validSourceIds.forEach((id) => {
                queryParams.append('id[]', id);
            });
            const endpoint = `/v1/activity?${queryParams.toString()}`;
            const { data } = await get(endpoint);
            return data;
        },
        enabled: validSourceIds.length > 0,
        refetchOnWindowFocus: false,
        retry: 1,
    });
    // Create a map of sourceId -> Activity for quick lookup
    const activitiesMap = useMemo(() => {
        const map = {};
        batchActivityResponse?.data?.forEach((activity) => {
            if (activity.id) {
                map[activity.id] = activity;
            }
        });
        return map;
    }, [batchActivityResponse]);
    // Compute source status for each source activity
    const statusMap = useMemo(() => {
        const map = {};
        validSourceIds.forEach((sourceId) => {
            const activity = activitiesMap[sourceId];
            if (!activity) {
                // Activity not found or still loading
                map[sourceId] = {
                    isLoadingSourceActivity: isLoadingActivities,
                    activityOrigin: ActivityOrigin.ORIGINAL,
                    isLoadingSupportedLocales: false,
                };
                return;
            }
            const activityData = activity.data;
            const currentVersion = parseInt(activity.version?.toString() || '1', 10);
            // Extract clone metadata from Activity metadata (not EcoaActivity)
            const cloneMetadata = activity.metadata?.clone;
            // Get source_id from activity metadata
            const sourceActivityId = cloneMetadata?.sourceId || activity.metadata?.source_id;
            const sourceLink = sourceActivityId ? `/library/activities/${sourceActivityId}` : undefined;
            const sourceWorkspaceId = cloneMetadata?.sourceEntity?.id;
            const sourceWorkspaceName = cloneMetadata?.sourceEntity?.name;
            // Create a Resource-like object for getActivityOrigin
            const activityResource = {
                version: activity.version?.toString() || '1',
                metadata: activity.metadata,
            };
            // Determine activity origin using getActivityOrigin function
            const activityOrigin = getActivityOrigin(activityResource);
            // Calculate source activity version from metadata
            let sourceActivityVersion;
            if (cloneMetadata?.sourceVersion) {
                sourceActivityVersion = cloneMetadata.sourceVersion.toString();
            }
            else {
                sourceActivityVersion = currentVersion.toString();
            }
            const status = {
                isLoadingSourceActivity: false,
                activityOrigin,
                isLoadingSupportedLocales: false,
                currentActivity: activityData,
                createdAt: activity.createdAt,
            };
            if (sourceActivityId) {
                status.sourceId = sourceActivityId;
                status.sourceLink = sourceLink;
            }
            if (sourceActivityVersion) {
                status.sourceActivityVersion = sourceActivityVersion;
            }
            if (sourceWorkspaceId) {
                status.sourceWorkspaceId = sourceWorkspaceId.toString();
            }
            if (sourceWorkspaceName) {
                status.sourceWorkspaceName = sourceWorkspaceName;
            }
            map[sourceId] = status;
        });
        return map;
    }, [validSourceIds, activitiesMap, isLoadingActivities]);
    return statusMap;
};
//# sourceMappingURL=use_activity_source_status_batch.hook.js.map